import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const langs = [
    { code: 'en', label: t('lang_en') },
    { code: 'ja', label: t('lang_ja') },
    { code: 'km', label: t('lang_km') },
  ];

  const navItems = [
    { key: 'nav_home', href: '#home' },
    { key: 'nav_about', href: '#about' },
    { key: 'nav_profile', href: '#profile' },
    { key: 'nav_business', href: '#business' },
    { key: 'nav_contact', href: '#contact' },
  ];

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#home" onClick={() => scrollTo('#home')} className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/a937ff05ce380c9fbb18eedcfdacca99/bfdd5981a66839fc1c1c14f01a886268.png"
              alt="LAVSTYLE Cambodia Logo"
              title="LAVSTYLE Cambodia-Japan International Business"
              className="h-10 md:h-12 w-auto object-contain"
            />
            <span className="hidden lg:inline text-navy-900 font-semibold text-sm whitespace-nowrap tracking-wide">
              LAVSTYLE (CAMBODIA) INTERNATIONAL CO., LTD.
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            <nav className="flex items-center gap-7" aria-label="Main Navigation">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(item.href);
                  }}
                  className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 hover:text-gold-600 ${
                    scrolled ? 'text-navy-800' : 'text-white/90'
                  }`}
                >
                  {t(item.key)}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-1 bg-navy-50 rounded-sm p-1" role="group" aria-label="Language Switcher">
              {langs.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all duration-200 whitespace-nowrap ${
                    i18n.language === lang.code
                      ? 'bg-navy-800 text-gold-300'
                      : scrolled
                      ? 'text-navy-600 hover:text-navy-800'
                      : 'text-white/70 hover:text-white'
                  }`}
                  aria-pressed={i18n.language === lang.code}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-md cursor-pointer"
            aria-label="Toggle mobile menu"
            aria-expanded={mobileOpen}
          >
            <i className={`ri-menu-line text-xl ${scrolled ? 'text-navy-800' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-xl">
          <nav className="px-4 py-4 flex flex-col gap-2" aria-label="Mobile Navigation">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(item.href);
                }}
                className="text-sm font-medium text-navy-800 py-2.5 hover:text-gold-600 transition-colors"
              >
                {t(item.key)}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100" role="group" aria-label="Language Switcher">
              {langs.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all duration-200 whitespace-nowrap ${
                    i18n.language === lang.code
                      ? 'bg-navy-800 text-gold-300'
                      : 'text-navy-600 hover:text-navy-800'
                  }`}
                  aria-pressed={i18n.language === lang.code}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
